// @ts-check
import React from 'react';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';

import BusComponent from '../BusComponent';
import Trigger from 'rc-trigger';
import { focusFirstChild } from '../../helpers/Util';
import LocationAnalysisCreateReportLoader from './LocationAnalysisCreateReportLoader';
import LocationAnalysisReports from './LocationAnalysisReports';
import { HelpTourTargets } from '../../enums/HelpTourDefinitions';

const popupAlignOptions = {
    points: ['tc', 'bc'],
    offset: [0, 10],
};

/**
 * @typedef Props
 * @property {string} mapInstanceId
 * @property {import('react-intl').intlShape} intl
 *
 * @typedef State
 * @property {Array.<import('../../types').FeaturedSurveyReport | import('../../types').FeaturedOlapReport>} featuredReports
 * @property {boolean} isUserStarterTier
 * @property {boolean} popupVisible
 *
 * @extends {BusComponent<Props, State>}
 */
class LocationAnalysisReportPopup extends BusComponent {
    constructor(props, context) {
        super(props, context);
        /** @type {State} */
        this.state = {
            featuredReports: [],
            isUserStarterTier: false,
            popupVisible: false,
        };
        this.boundAfterPopupRender = this.afterPopupRender.bind(this);
    }

    componentDidMount() {
        this.bindGluBusEvents({
            CUSTOM_MAP_SELECTION_FEATURED_REPORTS: this.onFeaturedReportsResponse,
            USER_INFO_GET_SUCCESS: this.onUserInfo,
        });

        this.emit('CUSTOM_MAP_SELECTION_FEATURED_REPORTS_REQUEST');
        this.emit('USER_INFO_GET_REQUEST', { source: this });
    }

    componentWillUnmount() {
        this.unbindGluBusEvents();
    }

    onUserInfo = (userInfo, source) => {
        if (source !== this) return;
        this.setState({
            isUserStarterTier: userInfo && userInfo.isStarterTierUser,
        });
    };

    /** @param {Array.<import('../../types').FeaturedSurveyReport | import('../../types').FeaturedOlapReport>} featuredReports */
    onFeaturedReportsResponse = featuredReports => {
        this.setState({ featuredReports });
    };

    afterPopupRender = visible => {
        if (visible) {
            focusFirstChild(this.popupContainer);
        }
    };

    handlePopupVisibleChange = (popupVisible) => {
        this.setState({ popupVisible });
    };

    closePopup = () => {
        this.setState({ popupVisible: false });
    };

    render() {
        const { featuredReports, isUserStarterTier } = this.state;

        return (
            <Trigger
                action={['click']}
                popupVisible={this.state.popupVisible}
                onPopupVisibleChange={this.handlePopupVisibleChange}
                onPopupAlign={this.boundAfterPopupRender}
                popup={
                    <div
                        className="menu-popup"
                        ref={e => {
                            this.popupContainer = e;
                        }}
                    >
                        <div className="report-popup">
                            <div className="report-popup__header">
                                <span>
                                    {this.props.intl.formatMessage({
                                        id: 'dataBrowser.downloadReport',
                                    })}
                                </span>
                                <button
                                    className="btn-icon radius-selector__modal-close"
                                    onClick={this.closePopup}
                                >
                                    <i className="material-icons">close</i>
                                </button>
                            </div>

                            <div className="report-popup__reports">
                                <LocationAnalysisReports
                                    mapInstanceId={this.props.mapInstanceId}
                                    reports={featuredReports}
                                    isUserStarterTier={isUserStarterTier}
                                />
                            </div>

                            <LocationAnalysisCreateReportLoader />
                        </div>
                    </div>
                }
                popupAlign={popupAlignOptions}
            >
                <div data-tourId={HelpTourTargets.INFO_PANEL_EXPORT_DATA}>
                    <button className="btn-chip" aria-haspopup>
                        <i className="material-symbols btn-chip__icon">download</i>
                        <span>{this.props.intl.formatMessage({ id: 'dataBrowser.report' })}</span>
                    </button>
                </div>
            </Trigger>
        );
    }
}

LocationAnalysisReportPopup.propTypes = {
    mapInstanceId: PropTypes.string.isRequired,
    intl: PropTypes.object.isRequired,
};

export default injectIntl(LocationAnalysisReportPopup);
